const QR_VERSIONS = [
  null,
  {
    size: 21,
    alignment: [],
    blocks: [{ count: 1, total: 26, data: 19 }],
  },
  {
    size: 25,
    alignment: [6, 18],
    blocks: [{ count: 1, total: 44, data: 34 }],
  },
  {
    size: 29,
    alignment: [6, 22],
    blocks: [{ count: 1, total: 70, data: 55 }],
  },
  {
    size: 33,
    alignment: [6, 26],
    blocks: [{ count: 1, total: 100, data: 80 }],
  },
  {
    size: 37,
    alignment: [6, 30],
    blocks: [{ count: 1, total: 134, data: 108 }],
  },
  {
    size: 41,
    alignment: [6, 34],
    blocks: [{ count: 2, total: 86, data: 68 }],
  },
  {
    size: 45,
    alignment: [6, 22, 38],
    blocks: [{ count: 2, total: 98, data: 78 }],
  },
  {
    size: 49,
    alignment: [6, 24, 42],
    blocks: [{ count: 2, total: 121, data: 97 }],
  },
  {
    size: 53,
    alignment: [6, 26, 46],
    blocks: [{ count: 2, total: 146, data: 116 }],
  },
  {
    size: 57,
    alignment: [6, 28, 50],
    blocks: [
      { count: 2, total: 86, data: 68 },
      { count: 2, total: 87, data: 69 },
    ],
  },
];

const EXP_TABLE = new Array(512);
const LOG_TABLE = new Array(256);

let tableValue = 1;

for (let index = 0; index < 255; index += 1) {
  EXP_TABLE[index] = tableValue;
  LOG_TABLE[tableValue] = index;

  tableValue <<= 1;

  if (tableValue & 0x100) {
    tableValue ^= 0x11d;
  }
}

for (let index = 255; index < 512; index += 1) {
  EXP_TABLE[index] = EXP_TABLE[index - 255];
}

const gfMultiply = (left, right) => {
  if (left === 0 || right === 0) {
    return 0;
  }

  return EXP_TABLE[LOG_TABLE[left] + LOG_TABLE[right]];
};

const multiplyPolynomials = (left, right) => {
  const result = new Array(left.length + right.length - 1).fill(0);

  for (let leftIndex = 0; leftIndex < left.length; leftIndex += 1) {
    for (let rightIndex = 0; rightIndex < right.length; rightIndex += 1) {
      result[leftIndex + rightIndex] ^= gfMultiply(
        left[leftIndex],
        right[rightIndex],
      );
    }
  }

  return result;
};

const createGeneratorPolynomial = (degree) => {
  let polynomial = [1];

  for (let index = 0; index < degree; index += 1) {
    polynomial = multiplyPolynomials(polynomial, [1, EXP_TABLE[index]]);
  }

  return polynomial;
};

const createErrorCorrection = (data, correctionLength) => {
  const generator = createGeneratorPolynomial(correctionLength);

  const message = [...data, ...new Array(correctionLength).fill(0)];

  for (let index = 0; index < data.length; index += 1) {
    const factor = message[index];

    if (factor === 0) {
      continue;
    }

    for (
      let generatorIndex = 0;
      generatorIndex < generator.length;
      generatorIndex += 1
    ) {
      message[index + generatorIndex] ^= gfMultiply(
        generator[generatorIndex],
        factor,
      );
    }
  }

  return message.slice(data.length);
};

class BitBuffer {
  constructor() {
    this.bits = [];
  }

  push(value, length) {
    for (let index = length - 1; index >= 0; index -= 1) {
      this.bits.push((value >>> index) & 1);
    }
  }

  get length() {
    return this.bits.length;
  }

  toBytes() {
    const bytes = [];

    for (let index = 0; index < this.bits.length; index += 8) {
      let byte = 0;

      for (let bitIndex = 0; bitIndex < 8; bitIndex += 1) {
        byte <<= 1;
        byte |= this.bits[index + bitIndex] || 0;
      }

      bytes.push(byte);
    }

    return bytes;
  }
}

const getDataCapacity = (version) =>
  QR_VERSIONS[version].blocks.reduce(
    (total, blockGroup) => total + blockGroup.count * blockGroup.data,
    0,
  );

const selectVersion = (byteLength) => {
  for (let version = 1; version < QR_VERSIONS.length; version += 1) {
    const characterCountBits = version <= 9 ? 8 : 16;
    const requiredBits = 4 + characterCountBits + byteLength * 8;
    const availableBits = getDataCapacity(version) * 8;

    if (requiredBits <= availableBits) {
      return version;
    }
  }

  throw new Error("The payment URL is too large for this local QR encoder.");
};

const createDataCodewords = (text, version) => {
  const bytes = Array.from(new TextEncoder().encode(text));
  const capacity = getDataCapacity(version);
  const capacityBits = capacity * 8;

  const buffer = new BitBuffer();

  // QR byte mode.
  buffer.push(0b0100, 4);

  // Character count.
  buffer.push(bytes.length, version <= 9 ? 8 : 16);

  bytes.forEach((byte) => {
    buffer.push(byte, 8);
  });

  const terminatorLength = Math.min(
    4,
    Math.max(0, capacityBits - buffer.length),
  );

  buffer.push(0, terminatorLength);

  while (buffer.length % 8 !== 0) {
    buffer.push(0, 1);
  }

  const codewords = buffer.toBytes();

  let useFirstPadByte = true;

  while (codewords.length < capacity) {
    codewords.push(useFirstPadByte ? 0xec : 0x11);
    useFirstPadByte = !useFirstPadByte;
  }

  return codewords;
};

const createFinalCodewords = (text, version) => {
  const versionInfo = QR_VERSIONS[version];
  const dataCodewords = createDataCodewords(text, version);

  const dataBlocks = [];
  const errorBlocks = [];

  let offset = 0;

  versionInfo.blocks.forEach((blockGroup) => {
    for (let blockIndex = 0; blockIndex < blockGroup.count; blockIndex += 1) {
      const blockData = dataCodewords.slice(offset, offset + blockGroup.data);

      offset += blockGroup.data;

      dataBlocks.push(blockData);

      errorBlocks.push(
        createErrorCorrection(blockData, blockGroup.total - blockGroup.data),
      );
    }
  });

  const result = [];

  const maximumDataLength = Math.max(
    ...dataBlocks.map((block) => block.length),
  );

  const maximumErrorLength = Math.max(
    ...errorBlocks.map((block) => block.length),
  );

  for (let index = 0; index < maximumDataLength; index += 1) {
    dataBlocks.forEach((block) => {
      if (index < block.length) {
        result.push(block[index]);
      }
    });
  }

  for (let index = 0; index < maximumErrorLength; index += 1) {
    errorBlocks.forEach((block) => {
      if (index < block.length) {
        result.push(block[index]);
      }
    });
  }

  return result;
};

const createMatrix = (size) =>
  Array.from({ length: size }, () => new Array(size).fill(null));

const createReservedMatrix = (size) =>
  Array.from({ length: size }, () => new Array(size).fill(false));

const setModule = (
  matrix,
  reserved,
  row,
  column,
  value,
  shouldReserve = true,
) => {
  if (
    row < 0 ||
    column < 0 ||
    row >= matrix.length ||
    column >= matrix.length
  ) {
    return;
  }

  matrix[row][column] = Boolean(value);

  if (shouldReserve) {
    reserved[row][column] = true;
  }
};

const addFinderPattern = (matrix, reserved, startRow, startColumn) => {
  for (let rowOffset = -1; rowOffset <= 7; rowOffset += 1) {
    for (let columnOffset = -1; columnOffset <= 7; columnOffset += 1) {
      const row = startRow + rowOffset;
      const column = startColumn + columnOffset;

      if (
        row < 0 ||
        column < 0 ||
        row >= matrix.length ||
        column >= matrix.length
      ) {
        continue;
      }

      const isInsideFinder =
        rowOffset >= 0 &&
        rowOffset <= 6 &&
        columnOffset >= 0 &&
        columnOffset <= 6;

      const isOuterBorder =
        rowOffset === 0 ||
        rowOffset === 6 ||
        columnOffset === 0 ||
        columnOffset === 6;

      const isInnerSquare =
        rowOffset >= 2 &&
        rowOffset <= 4 &&
        columnOffset >= 2 &&
        columnOffset <= 4;

      const isDark = isInsideFinder && (isOuterBorder || isInnerSquare);

      setModule(matrix, reserved, row, column, isDark);
    }
  }
};

const addAlignmentPattern = (matrix, reserved, centerRow, centerColumn) => {
  for (let rowOffset = -2; rowOffset <= 2; rowOffset += 1) {
    for (let columnOffset = -2; columnOffset <= 2; columnOffset += 1) {
      const distance = Math.max(Math.abs(rowOffset), Math.abs(columnOffset));

      setModule(
        matrix,
        reserved,
        centerRow + rowOffset,
        centerColumn + columnOffset,
        distance !== 1,
      );
    }
  }
};

const addFunctionPatterns = (matrix, reserved, version) => {
  const size = matrix.length;

  addFinderPattern(matrix, reserved, 0, 0);
  addFinderPattern(matrix, reserved, 0, size - 7);
  addFinderPattern(matrix, reserved, size - 7, 0);

  // Timing patterns.
  for (let index = 8; index < size - 8; index += 1) {
    setModule(matrix, reserved, 6, index, index % 2 === 0);

    setModule(matrix, reserved, index, 6, index % 2 === 0);
  }

  // Alignment patterns.
  const alignmentPositions = QR_VERSIONS[version].alignment;

  alignmentPositions.forEach((row) => {
    alignmentPositions.forEach((column) => {
      const overlapsFinder =
        (row === 6 && column === 6) ||
        (row === 6 && column === size - 7) ||
        (row === size - 7 && column === 6);

      if (!overlapsFinder) {
        addAlignmentPattern(matrix, reserved, row, column);
      }
    });
  });

  // Reserve vertical format information area.
  for (let index = 0; index < 9; index += 1) {
    if (index !== 6) {
      reserved[index][8] = true;
    }
  }

  for (let index = size - 8; index < size; index += 1) {
    reserved[index][8] = true;
  }

  // Reserve horizontal format information area.
  for (let index = 0; index < 9; index += 1) {
    if (index !== 6) {
      reserved[8][index] = true;
    }
  }

  for (let index = size - 8; index < size; index += 1) {
    reserved[8][index] = true;
  }

  // Required fixed dark module.
  setModule(matrix, reserved, size - 8, 8, true);

  // Reserve version information areas.
  if (version >= 7) {
    for (let row = 0; row < 6; row += 1) {
      for (let column = 0; column < 3; column += 1) {
        reserved[row][size - 11 + column] = true;
        reserved[size - 11 + column][row] = true;
      }
    }
  }
};

const getBchDigit = (inputValue) => {
  let value = inputValue;
  let digit = 0;

  while (value !== 0) {
    digit += 1;
    value >>>= 1;
  }

  return digit;
};

const getFormatBits = (maskPattern) => {
  // Error-correction level L uses the bit value 01.
  const data = (0b01 << 3) | maskPattern;

  let value = data << 10;
  const generator = 0x537;

  while (getBchDigit(value) - getBchDigit(generator) >= 0) {
    value ^= generator << (getBchDigit(value) - getBchDigit(generator));
  }

  return ((data << 10) | value) ^ 0x5412;
};

const getVersionBits = (version) => {
  let value = version << 12;
  const generator = 0x1f25;

  while (getBchDigit(value) - getBchDigit(generator) >= 0) {
    value ^= generator << (getBchDigit(value) - getBchDigit(generator));
  }

  return (version << 12) | value;
};

const addFormatInformation = (matrix, reserved, maskPattern) => {
  const size = matrix.length;
  const formatBits = getFormatBits(maskPattern);

  for (let index = 0; index < 15; index += 1) {
    const isDark = ((formatBits >>> index) & 1) === 1;

    /*
     * Vertical format information.
     */
    if (index < 6) {
      setModule(matrix, reserved, index, 8, isDark);
    } else if (index < 8) {
      setModule(matrix, reserved, index + 1, 8, isDark);
    } else {
      setModule(matrix, reserved, size - 15 + index, 8, isDark);
    }

    /*
     * Horizontal format information.
     */
    if (index < 8) {
      setModule(matrix, reserved, 8, size - index - 1, isDark);
    } else if (index === 8) {
      setModule(matrix, reserved, 8, 7, isDark);
    } else {
      setModule(matrix, reserved, 8, 15 - index - 1, isDark);
    }
  }

  setModule(matrix, reserved, size - 8, 8, true);
};

const addVersionInformation = (matrix, reserved, version) => {
  if (version < 7) {
    return;
  }

  const size = matrix.length;
  const versionBits = getVersionBits(version);

  for (let index = 0; index < 18; index += 1) {
    const isDark = ((versionBits >>> index) & 1) === 1;

    const row = Math.floor(index / 3);
    const column = (index % 3) + size - 11;

    setModule(matrix, reserved, row, column, isDark);

    setModule(matrix, reserved, column, row, isDark);
  }
};

const getMask = (maskPattern, row, column) => {
  switch (maskPattern) {
    case 0:
      return (row + column) % 2 === 0;

    case 1:
      return row % 2 === 0;

    case 2:
      return column % 3 === 0;

    case 3:
      return (row + column) % 3 === 0;

    case 4:
      return (Math.floor(row / 2) + Math.floor(column / 3)) % 2 === 0;

    case 5:
      return ((row * column) % 2) + ((row * column) % 3) === 0;

    case 6:
      return (((row * column) % 2) + ((row * column) % 3)) % 2 === 0;

    case 7:
      return (((row + column) % 2) + ((row * column) % 3)) % 2 === 0;

    default:
      return false;
  }
};

const codewordsToBits = (codewords) => {
  const bits = [];

  codewords.forEach((codeword) => {
    for (let bitIndex = 7; bitIndex >= 0; bitIndex -= 1) {
      bits.push((codeword >>> bitIndex) & 1);
    }
  });

  return bits;
};

const addData = (matrix, reserved, codewords, maskPattern) => {
  const bits = codewordsToBits(codewords);
  const size = matrix.length;

  let bitIndex = 0;
  let row = size - 1;
  let direction = -1;

  for (let column = size - 1; column > 0; column -= 2) {
    if (column === 6) {
      column -= 1;
    }

    while (true) {
      for (let columnOffset = 0; columnOffset < 2; columnOffset += 1) {
        const currentColumn = column - columnOffset;

        if (reserved[row][currentColumn]) {
          continue;
        }

        const dataBit = bits[bitIndex] || 0;
        const maskBit = getMask(maskPattern, row, currentColumn);

        matrix[row][currentColumn] = Boolean(dataBit ^ Number(maskBit));

        bitIndex += 1;
      }

      row += direction;

      if (row < 0 || row >= size) {
        row -= direction;
        direction *= -1;
        break;
      }
    }
  }
};

const calculatePenalty = (matrix) => {
  const size = matrix.length;

  let penalty = 0;

  const scoreLine = (line) => {
    let score = 0;
    let currentValue = line[0];
    let runLength = 1;

    for (let index = 1; index < line.length; index += 1) {
      if (line[index] === currentValue) {
        runLength += 1;
      } else {
        if (runLength >= 5) {
          score += 3 + (runLength - 5);
        }

        currentValue = line[index];
        runLength = 1;
      }
    }

    if (runLength >= 5) {
      score += 3 + (runLength - 5);
    }

    return score;
  };

  // Long repeated runs.
  for (let row = 0; row < size; row += 1) {
    penalty += scoreLine(matrix[row]);

    const column = [];

    for (let columnIndex = 0; columnIndex < size; columnIndex += 1) {
      column.push(matrix[columnIndex][row]);
    }

    penalty += scoreLine(column);
  }

  // 2x2 blocks.
  for (let row = 0; row < size - 1; row += 1) {
    for (let column = 0; column < size - 1; column += 1) {
      const value = matrix[row][column];

      if (
        matrix[row][column + 1] === value &&
        matrix[row + 1][column] === value &&
        matrix[row + 1][column + 1] === value
      ) {
        penalty += 3;
      }
    }
  }

  const finderLikePattern = [true, false, true, true, true, false, true];

  const matchesPattern = (line, startIndex) =>
    finderLikePattern.every(
      (expectedValue, index) => line[startIndex + index] === expectedValue,
    );

  const hasFourWhiteBefore = (line, startIndex) =>
    startIndex >= 4 &&
    line.slice(startIndex - 4, startIndex).every((value) => value === false);

  const hasFourWhiteAfter = (line, startIndex) =>
    startIndex + 11 <= line.length &&
    line
      .slice(startIndex + 7, startIndex + 11)
      .every((value) => value === false);

  for (let index = 0; index < size; index += 1) {
    const rowLine = matrix[index];
    const columnLine = matrix.map((row) => row[index]);

    [rowLine, columnLine].forEach((line) => {
      for (let startIndex = 0; startIndex <= size - 7; startIndex += 1) {
        if (
          matchesPattern(line, startIndex) &&
          (hasFourWhiteBefore(line, startIndex) ||
            hasFourWhiteAfter(line, startIndex))
        ) {
          penalty += 40;
        }
      }
    });
  }

  let darkModules = 0;

  matrix.forEach((row) => {
    row.forEach((isDark) => {
      if (isDark) {
        darkModules += 1;
      }
    });
  });

  const totalModules = size * size;
  const darkPercentage = (darkModules * 100) / totalModules;

  penalty += Math.floor(Math.abs(darkPercentage - 50) / 5) * 10;

  return penalty;
};

const buildQrMatrix = (text, version, maskPattern) => {
  const size = QR_VERSIONS[version].size;

  const matrix = createMatrix(size);
  const reserved = createReservedMatrix(size);
  const codewords = createFinalCodewords(text, version);

  addFunctionPatterns(matrix, reserved, version);
  addData(matrix, reserved, codewords, maskPattern);
  addFormatInformation(matrix, reserved, maskPattern);
  addVersionInformation(matrix, reserved, version);

  return matrix;
};

export const generateQrMatrix = (text) => {
  if (typeof text !== "string" || text.trim().length === 0) {
    throw new Error("QR content is required.");
  }

  const byteLength = new TextEncoder().encode(text).length;

  const version = selectVersion(byteLength);

  let bestMatrix = null;
  let bestPenalty = Number.POSITIVE_INFINITY;

  for (let maskPattern = 0; maskPattern < 8; maskPattern += 1) {
    const matrix = buildQrMatrix(text, version, maskPattern);

    const penalty = calculatePenalty(matrix);

    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      bestMatrix = matrix;
    }
  }

  if (!bestMatrix) {
    throw new Error("Unable to create QR matrix.");
  }

  return bestMatrix;
};

export const drawQrOnCanvas = (
  canvas,
  matrix,
  {
    maximumSize = 420,
    quietZoneModules = 4,
    foreground = "#000000",
    background = "#ffffff",
  } = {},
) => {
  if (!canvas || !Array.isArray(matrix) || matrix.length === 0) {
    return;
  }

  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Canvas is not supported in this browser.");
  }

  const moduleCount = matrix.length;

  const totalModuleCount = moduleCount + quietZoneModules * 2;

  const parentWidth = canvas.parentElement?.clientWidth || maximumSize;

  const parentHeight = canvas.parentElement?.clientHeight || parentWidth;

  const availableSize = Math.min(
    maximumSize,
    Math.floor(parentWidth),
    Math.floor(parentHeight),
  );

  const moduleSize = Math.floor(availableSize / totalModuleCount);

  if (moduleSize < 1) {
    throw new Error("The QR container is too small.");
  }

  const renderedSize = totalModuleCount * moduleSize;

  const pixelRatio = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));

  canvas.width = renderedSize * pixelRatio;
  canvas.height = renderedSize * pixelRatio;

  canvas.style.width = `${renderedSize}px`;
  canvas.style.height = `${renderedSize}px`;
  canvas.style.maxWidth = "100%";
  canvas.style.maxHeight = "100%";
  canvas.style.display = "block";
  canvas.style.flexShrink = "0";

  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

  context.imageSmoothingEnabled = false;

  context.fillStyle = background;
  context.fillRect(0, 0, renderedSize, renderedSize);

  context.fillStyle = foreground;

  const qrStart = quietZoneModules * moduleSize;

  matrix.forEach((row, rowIndex) => {
    row.forEach((isDark, columnIndex) => {
      if (!isDark) {
        return;
      }

      context.fillRect(
        qrStart + columnIndex * moduleSize,
        qrStart + rowIndex * moduleSize,
        moduleSize,
        moduleSize,
      );
    });
  });
};
